/**
 * `/$tenantSlug/settings/modules` — modules settings sub-route.
 *
 * Per KEN_LAYER_7 §7.7. Lists the modules installed in this build
 * (sourced from the build-time @ken/vite-plugin-ken-modules registry,
 * per ADR-040) and shows which are enabled for the active tenant.
 * The owner toggles each module on or off; the backend persists the
 * change and the next session refresh picks up the new
 * `enabled_modules` array.
 *
 * Pessimistic update: the toggle disables itself while the request is
 * in flight; only the response triggers the local-state change so the
 * UI never shows a state the backend disagrees with.
 */

import { RequirePermission, useAuthStore } from "@ken/auth";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  Skeleton,
} from "@ken/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactElement } from "react";

import { MODULES } from "../generated/modules";

export const Route = createFileRoute("/$tenantSlug/settings/modules")({
  component: ModulesSettingsRoute,
});

/**
 * Hard permission gate (defense-in-depth): enabling/disabling modules is a
 * tenant-settings admin surface and must require `shell.settings.view`. This
 * route had no gate of its own, so any user (incl. an advisor) could reach it
 * by URL. RequirePermission renders an accessible "no access" region instead.
 * ADR-0090 (advisor envelope).
 */
function ModulesSettingsRoute(): ReactElement {
  return (
    <RequirePermission slug="shell.settings.view">
      <ModulesSettingsContent />
    </RequirePermission>
  );
}

interface ModuleRowData {
  id: string;
  displayName: string;
  description: string;
  kind: string;
}

function moduleRowsFromRegistry(): ModuleRowData[] {
  return MODULES.map((m) => {
    const identity = (m.manifest as unknown as {
      identity?: { display_name?: string; description?: string; kind?: string };
    }).identity;
    return {
      id: m.name,
      displayName: identity?.display_name ?? m.name,
      description: identity?.description ?? "",
      kind: identity?.kind ?? "feature",
    };
  });
}

function ModulesSettingsContent(): ReactElement {
  const session = useAuthStore((s) => s.session);
  const setSession = useAuthStore((s) => s.setSession);
  const enabled = new Set(session?.enabledModules ?? []);

  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rows = moduleRowsFromRegistry();

  const refreshSession = async (): Promise<void> => {
    try {
      const res = await fetch("/api/shell/auth/session", { credentials: "include" });
      if (!res.ok) return;
      const body = (await res.json()) as {
        user: {
          ken_user_id: string;
          email: string;
          display_name: string | null;
          roles: string[];
        };
        tenant: { id: string; enabled_modules: string[]; currency?: string } | null;
        permissions: string[];
        permissions_version: number;
      };
      if (session?.user) {
        setSession({
          user: {
            ken_user_id: session.user.ken_user_id,
            email: session.user.email,
            display_name: session.user.display_name,
            roles: session.user.roles,
          },
          tenantId: body.tenant?.id ?? session.tenantId,
          permissions: new Set(body.permissions),
          permissions_version: body.permissions_version,
          enabledModules: body.tenant?.enabled_modules ?? [],
          tenant: {
            // `session.tenant` is optional (AuthSession.tenant?); guard it so a
            // session without a resolved tenant block (e.g. some test fixtures
            // / a pre-tenant edge) doesn't throw. Fall back to the same default
            // useCurrency() uses.
            currency: body.tenant?.currency ?? session.tenant?.currency ?? "CAD",
          },
        });
      }
    } catch {
      // Refresh failure is non-fatal — the next 60s poll picks it up.
    }
  };

  const toggle = async (moduleId: string, currentlyEnabled: boolean): Promise<void> => {
    setPendingId(moduleId);
    setError(null);
    try {
      const endpoint = currentlyEnabled ? "disable" : "enable";
      const res = await fetch(`/api/shell/settings/modules/${moduleId}/${endpoint}`, {
        method: "POST",
        credentials: "include",
      });
      if (res.status === 403) {
        setError("You don't have permission to change this.");
        return;
      }
      if (!res.ok) {
        // ADR-0274: a refused switch (a module others depend on) comes with
        // one plain sentence in detail.message; show it as written.
        const message = await res
          .json()
          .then((body: { detail?: { message?: unknown } }) => body?.detail?.message)
          .catch(() => undefined);
        setError(
          typeof message === "string" && message !== ""
            ? message
            : `Couldn't ${endpoint} ${moduleId} (status ${res.status}).`,
        );
        return;
      }
      await refreshSession();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setPendingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="shell-settings-modules-page">
      <header>
        <div className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-text-faint)] font-semibold">
          Tenant · Settings
        </div>
        <h1 className="text-[26px] font-bold tracking-tight">Modules</h1>
        <p className="text-sm text-[var(--color-text-soft)] mt-1">
          Turn modules on or off for this tenant. Disabled modules stay installed
          in the platform but their pages, navigation entries, and widgets stop
          appearing.
        </p>
      </header>

      {error !== null && (
        <div
          role="alert"
          data-testid="shell-settings-modules-error"
          className="text-sm text-[var(--color-accent-red)] px-3 py-2 rounded-[var(--radius-sm)] bg-[oklch(99%_0.04_25_/_0.4)] border border-[var(--color-accent-red)]"
        >
          {error}
        </div>
      )}

      {rows.length === 0 ? (
        <Card surface="glass-strong" padding="lg">
          <CardContent>
            <div
              data-testid="shell-settings-modules-empty"
              className="text-sm text-[var(--color-text-soft)] text-center py-8"
            >
              No modules installed in this build. Add modules under{" "}
              <code className="font-mono text-xs">modules/</code> and rebuild.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-3" data-testid="shell-settings-modules-list">
          {rows.map((row) => {
            const isEnabled = enabled.has(row.id);
            const isPending = pendingId === row.id;
            return (
              <Card
                key={row.id}
                surface="glass-strong"
                data-testid={`shell-settings-modules-row-${row.id}`}
              >
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <CardTitle className="truncate">{row.displayName}</CardTitle>
                      <Chip tone={row.kind === "agent" ? "amber" : "default"}>{row.kind}</Chip>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Chip tone={isEnabled ? "green" : "red"}>
                        {isEnabled ? "Enabled" : "Disabled"}
                      </Chip>
                      <Button
                        type="button"
                        variant={isEnabled ? "ghost" : "primary"}
                        size="sm"
                        data-testid={`shell-settings-modules-row-${row.id}-toggle`}
                        disabled={isPending}
                        onClick={() => void toggle(row.id, isEnabled)}
                      >
                        {isPending
                          ? "Saving…"
                          : isEnabled
                            ? "Disable"
                            : "Enable"}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-[var(--color-text-soft)]">
                    {row.description || "—"}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pending placeholder shape — preserved for the next render when
          MODULES is empty in a fresh build (registry not yet generated). */}
      <div className="hidden" data-testid="shell-settings-modules-skeleton-anchor">
        <Skeleton className="h-16 w-full" />
      </div>
    </div>
  );
}
